import { inject, Injectable } from '@angular/core';

import {
  ANALYTICS_EVENTS,
  EVENT_CLICK_TYPE,
  HEADER_MENU,
  HEADER_TOP_MENU,
  MenuSection,
  SecondaryNavigationID,
  USER_LOGGED_IN_STATUS
} from '../../../constants/utils';
import { adobeClickEventAnalyticsMocks } from '../../../mocks/utils';
import {
  DashboardResponse,
  loginAndGetDashboardResponse
} from '../../../models/login';
import {
  ClickEventAnalytics,
  PageLoadAnalytics
} from '../../../models/utils/analytics.interfaces';
import { UserSessionService } from '../../login/data-services/user-session/user-session.service';
import { AnalyticsService } from '../analytics/analytics.service';

declare let idp: Record<string, unknown>;

@Injectable({
  providedIn: 'root'
})
export class AdobeDataLayerService {
  private userSession = inject(UserSessionService);
  analytics = inject(AnalyticsService);

  setUserDataInAnalytics(data: ClickEventAnalytics | PageLoadAnalytics): void {
    this.userSession
      .getUserSession()
      .subscribe((userStatus: DashboardResponse) => {
        data = {
          ...data,
          ...{
            user: {
              attributes: {
                is_logged_in: userStatus?.loggedIn
                  ? USER_LOGGED_IN_STATUS.AUTHENTICATED
                  : USER_LOGGED_IN_STATUS.UNAUTHENTICATED
              }
            }
          }
        };
        this.analytics.setAnalytics(data);
      });
  }

  updateAnalytics(
    url: string,
    navItem: keyof typeof HEADER_MENU,
    subNavItem = ''
  ): void {
    const headerMenu = this.getHeaderMenu(navItem, subNavItem);
    const { NAME, PAGE_SECTION } = headerMenu;
    const isSecondaryNav = this.isSecondaryNavId(url);
    let data = adobeClickEventAnalyticsMocks;
    data = {
      ...data,
      event: ANALYTICS_EVENTS.CLICK,
      event_detail: ANALYTICS_EVENTS.NAV_CLICK,
      click: {
        name: NAME,
        type: EVENT_CLICK_TYPE.BUTTON,
        page_section: PAGE_SECTION,
        action: isSecondaryNav
          ? ANALYTICS_EVENTS.NAV_CLICK
          : `${url} ${ANALYTICS_EVENTS.NAV_CLICK}`
      }
    };

    this.setUserDataInAnalytics(data);
  }

  private getHeaderMenu(
    navItem: keyof typeof HEADER_MENU,
    subNavItem: string
  ): MenuSection {
    if (subNavItem !== '') {
      const navSection = HEADER_MENU[navItem];
      return navSection[
        subNavItem as keyof typeof navSection
      ] as unknown as MenuSection;
    }
    return HEADER_MENU[navItem] as MenuSection;
  }

  private isSecondaryNavId(url: string): url is SecondaryNavigationID {
    return url === 'secondarynav' || url === 'sky-miles' || url === 'more';
  }

  appNavLinksAnalytics(
    navItem: SecondaryNavigationID,
    secondaryNav: string,
    url: string
  ): void {
    const menus = {
      secondarynav: HEADER_TOP_MENU.TRAVEL_INFO,
      'sky-miles': HEADER_TOP_MENU.SKYMILES,
      more: HEADER_TOP_MENU.NEED_HELP
    };
    let data = adobeClickEventAnalyticsMocks;
    const { PAGE_SECTION } = HEADER_MENU[menus[navItem]];
    const lowerCaseSecondaryNav = secondaryNav.toLowerCase();
    data = {
      ...data,
      event: ANALYTICS_EVENTS.CLICK,
      event_detail: ANALYTICS_EVENTS.NAV_CLICK,
      click: {
        name: lowerCaseSecondaryNav,
        type: EVENT_CLICK_TYPE.LINK,
        page_section: `${PAGE_SECTION}:${lowerCaseSecondaryNav}`,
        action: url
      }
    };

    this.setUserDataInAnalytics(data);
  }

  setDdcfbDataForStandalonePage(
    response: Partial<DashboardResponse> | loginAndGetDashboardResponse
  ) {
    const dataLayer = idp['dataLayer'] as Record<string, unknown> | undefined;
    const digitalData = dataLayer?.['digitalData'] as
      | Record<string, unknown>
      | undefined;
    const user = digitalData?.['user'] as Record<string, unknown> | undefined;
    const dataObject = user?.['ddcfbData'] as
      | Record<string, unknown>
      | undefined;

    if (dataObject) {
      dataObject['crprtAgrmntTypeCode'] = response['crprtAgrmntTypeCode'];
      dataObject['pgmType'] = response['pgmType'];
      dataObject['pgm'] = response['program'];
      dataObject['permissions'] = response['permType'];
    }
  }
}
